import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FetchMedia, ProductCatalogue } from "../../../hooks/APIHooks";
import {
  FcAddDatabase,
  FcSearch,
  FcEditImage,
  FcDeleteDatabase,
  FcRefresh,
  FcPicture,
  FcCancel,
  FcInfo,
  FcGallery,
  FcOpenedFolder,
  FcImageFile,
  FcDocument,
} from "react-icons/fc";

export default function ProductCatalogueMain() {
  const [catalogues, setCatalogues] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [form, setForm] = useState({
    catalogue_name: "",
    description: "",
    catalogue_image: "",
    image_name: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showImageDropdown, setShowImageDropdown] = useState(false);

  // Function to get proper media URL
  const getMediaUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;

    const cleanPath = path.replace(/^(\.?\/?)?/, "");

    let baseUrl;
    const { hostname, port, protocol } = window.location;

    if (hostname === "localhost" && port === "3000") {
      baseUrl = `${protocol}//${hostname}`;
    } else {
      baseUrl = window.location.origin;
    }

    return `${baseUrl}/apis/${cleanPath}`;
  };

  useEffect(() => {
    fetchCatalogues();
    fetchMediaFiles();
  }, []);

  const fetchCatalogues = async () => {
    try {
      setLoading(true);
      const response = await axios.get(ProductCatalogue);
      setCatalogues(response.data);
      setError("");
    } catch (error) {
      console.error("Error fetching catalogues", error);
      setError("Failed to fetch catalogues");
    } finally {
      setLoading(false);
    }
  };

  const fetchMediaFiles = async () => {
    try {
      const response = await axios.get(FetchMedia);
      if (response.data.success && Array.isArray(response.data.files)) {
        setMediaFiles(response.data.files);
      } else {
        console.error("Unexpected response structure:", response.data);
        setMediaFiles([]);
      }
    } catch (error) {
      console.error("Error fetching media files", error);
      setMediaFiles([]);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setLoading(true);
      if (editingId) {
        await axios.put(ProductCatalogue, { id: editingId, ...form });
        setSuccess("Catalogue updated successfully!");
      } else {
        await axios.post(ProductCatalogue, form);
        setSuccess("Catalogue added successfully!");
      }
      fetchCatalogues();
      resetForm();
    } catch (error) {
      console.error("Error saving catalogue", error);
      setError(error.response?.data?.error || "Failed to save catalogue");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      catalogue_name: "",
      description: "",
      catalogue_image: "",
      image_name: "",
    });
    setEditingId(null);
    setShowImageDropdown(false);
  };

  const handleEdit = (item) => {
    setForm({
      catalogue_name: item.catalogue_name,
      description: item.description,
      catalogue_image: item.catalogue_image,
      image_name: item.image_name || "",
    });
    setEditingId(item.catalogue_id);
    setError("");
    setSuccess("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this catalogue?")) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(ProductCatalogue, { data: { id } });
      setSuccess("Catalogue deleted successfully!");
      fetchCatalogues();
    } catch (error) {
      console.error("Error deleting catalogue", error);
      setError(error.response?.data?.error || "Failed to delete catalogue");
    } finally {
      setLoading(false);
    }
  };

  const filteredCatalogues = catalogues.filter(
    (item) =>
      item.catalogue_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FcOpenedFolder className="text-2xl" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-800">
                  Product Catalogues
                </h1>
                <p className="text-sm text-gray-600">
                  Manage your product catalogues and collections
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={fetchCatalogues}
                className="flex items-center space-x-2 bg-white text-gray-700 px-3 py-2 rounded-lg border hover:bg-gray-50 transition-colors text-sm"
              >
                <FcRefresh />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            {
              to: "/media",
              label: "Media Library",
              icon: FcGallery,
              desc: "Manage images",
            },
            {
              to: "/products",
              label: "Products",
              icon: FcDocument,
              desc: "Product items",
            },
            {
              to: "/categories",
              label: "Categories",
              icon: FcOpenedFolder,
              desc: "Categories",
            },
            {
              to: "/catalogues",
              label: "Catalogues",
              icon: FcPicture,
              desc: "Collections",
            },
          ].map((item, index) => (
            <Link
              key={index}
              to={item.to}
              className="bg-white p-3 rounded-lg border hover:shadow transition-all group"
            >
              <div className="flex items-center space-x-2">
                <div className="p-1 rounded bg-gray-50">
                  <item.icon className="text-lg" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-sm">
                    {item.label}
                  </p>
                  <p className="text-xs text-gray-600">{item.desc}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Success and Error Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <FcInfo className="mr-2" />
              <span className="text-sm">{success}</span>
            </div>
            <button
              onClick={() => setSuccess("")}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <FcInfo className="mr-2" />
              <span className="text-sm">{error}</span>
            </div>
            <button
              onClick={() => setError("")}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Add/Edit Form */}
        <div className="bg-white p-4 rounded-lg border mb-4">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <FcAddDatabase className="mr-2" />
            {editingId ? "Update Catalogue" : "Add New Catalogue"}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Catalogue Name *
                </label>
                <input
                  type="text"
                  name="catalogue_name"
                  placeholder="Enter catalogue name"
                  value={form.catalogue_name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Catalogue Image
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowImageDropdown(!showImageDropdown)}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm text-left flex items-center justify-between"
                  >
                    {form.catalogue_image ? (
                      <div className="flex items-center space-x-2">
                        <img
                          src={getMediaUrl(form.catalogue_image)}
                          alt="catalogue"
                          className="w-8 h-8 object-cover rounded"
                          onError={(e) => {
                            e.target.src =
                              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iMTYiIHk9IjE2IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzlhOWJhYiI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+";
                          }}
                        />
                        <span className="truncate text-xs">
                          {form.image_name}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-500">Select Image</span>
                    )}
                    <FcImageFile className="text-sm" />
                  </button>

                  {showImageDropdown && (
                    <div className="absolute top-full left-0 right-0 bg-white shadow-lg border rounded-lg mt-1 max-h-60 overflow-y-auto z-20">
                      {mediaFiles.length === 0 ? (
                        <div className="p-3 text-gray-500 text-center text-xs">
                          No images available
                        </div>
                      ) : (
                        mediaFiles.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center p-2 hover:bg-gray-100 cursor-pointer border-b"
                            onClick={() => {
                              setForm({
                                ...form,
                                catalogue_image: file.file_path,
                                image_name: file.file_name,
                              });
                              setShowImageDropdown(false);
                            }}
                          >
                            <img
                              src={getMediaUrl(file.file_path)}
                              alt={file.file_name}
                              className="w-8 h-8 object-cover mr-2 rounded"
                              onError={(e) => {
                                e.target.src =
                                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iMTYiIHk9IjE2IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzlhOWJhYiI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+";
                              }}
                            />
                            <span className="text-xs truncate flex-1">
                              {file.file_name}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                name="description"
                placeholder="Enter catalogue description"
                value={form.description}
                onChange={handleChange}
                rows="3"
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 text-white py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center space-x-2 text-sm ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <FcAddDatabase />
                    <span>
                      {editingId ? "Update Catalogue" : "Add Catalogue"}
                    </span>
                  </>
                )}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm flex items-center space-x-1"
                >
                  <FcCancel />
                  <span>Cancel</span>
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-lg border mb-4">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                <FcSearch className="mr-1" />
                Search Catalogues
              </label>
              <input
                type="text"
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div className="text-xs text-gray-600 bg-gray-100 px-3 py-1.5 rounded">
              {filteredCatalogues.length} of {catalogues.length} catalogues
            </div>
          </div>
        </div>

        {/* Catalogues Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h3 className="font-bold text-gray-800 flex items-center space-x-2 text-sm">
              <FcOpenedFolder />
              <span>Product Catalogues</span>
              <span className="text-gray-500 font-normal">
                ({filteredCatalogues.length})
              </span>
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Image
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && catalogues.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-gray-600">
                          Loading catalogues...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredCatalogues.length > 0 ? (
                  filteredCatalogues.map((item) => (
                    <tr
                      key={item.catalogue_id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {item.catalogue_image ? (
                          <img
                            src={getMediaUrl(item.catalogue_image)}
                            alt="catalogue"
                            className="w-12 h-12 object-cover rounded-lg"
                            onError={(e) => {
                              e.target.src =
                                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iMjQiIHk9IjI0IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5YTliYWIiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==";
                            }}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <FcPicture className="text-lg" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {item.catalogue_name}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600 max-w-md truncate">
                          {item.description}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleEdit(item)}
                            className="bg-blue-100 text-blue-600 p-1.5 rounded hover:bg-blue-200 transition-colors"
                            title="Edit"
                          >
                            <FcEditImage className="text-xs" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.catalogue_id)}
                            className="bg-red-100 text-red-600 p-1.5 rounded hover:bg-red-200 transition-colors"
                            title="Delete"
                          >
                            <FcDeleteDatabase className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center">
                      <FcOpenedFolder className="text-3xl mx-auto mb-2" />
                      <h3 className="font-semibold text-gray-600 mb-1 text-sm">
                        No catalogues found
                      </h3>
                      <p className="text-gray-500 mb-3 text-xs">
                        {searchTerm
                          ? "Adjust your search"
                          : "Add your first catalogue"}
                      </p>
                      <button
                        onClick={resetForm}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                      >
                        Add Catalogue
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
